import { useEffect, useState, useCallback, useRef } from "react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, Download, Upload, ShieldCheck, ShieldAlert, Database, Lock, UserCog, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const EMPTY = { username: "", name: "", email: "", password: "", role: "operatore" };

export default function Impostazioni() {
  const { isAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    const { data } = await api.get("/users");
    setUsers(data);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createUser = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const delUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportData = (formato) => {
    const url = `${API}/export/clienti?formato=${formato}`;
    // Cookie-authenticated download
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", `clienti.${formato}`);
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("Esportazione non consentita"); return r.blob(); })
      .then((blob) => {
        const objUrl = window.URL.createObjectURL(blob);
        a.href = objUrl;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success(`Esportazione ${formato.toUpperCase()} completata`);
      })
      .catch((e) => toast.error(e.message));
  };

  const exportJson = (endpoint, filename) => {
    fetch(`${API}${endpoint}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("Esportazione non consentita"); return r.blob(); })
      .then((blob) => {
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(u);
        toast.success("Esportazione GDPR completata");
      })
      .catch((e) => toast.error(e.message));
  };

  const importRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const onImportSelected = (e) => {
    const f = e.target.files?.[0];
    if (f) setPendingFile(f);
    if (importRef.current) importRef.current.value = "";
  };

  const doImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post("/import/completo", fd);
      toast.success(`Importazione completata: ${data.totale} record ripristinati`);
      setPendingFile(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center space-y-4">
        <div className="h-16 w-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto"><Lock className="h-8 w-8" /></div>
        <h1 className="font-head text-2xl font-bold text-slate-900">Accesso riservato</h1>
        <p className="text-slate-500">Le impostazioni di sicurezza, la gestione utenti e l'esportazione dati sono riservate agli amministratori.</p>
        <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100"><UserCog className="h-3.5 w-3.5 mr-1" /> Operatore</Badge>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-slate-900">Impostazioni</h1>
        <p className="text-sm text-slate-500 mt-1">Gestione utenti, esportazione dati e sicurezza.</p>
      </div>

      {/* Export */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-center gap-3 mb-1"><Download className="h-5 w-5 text-[#1E40AF]" /><h2 className="font-head text-lg font-semibold">Esportazione dati clienti</h2></div>
        <p className="text-sm text-slate-500 mb-4">Solo gli amministratori possono esportare. Ogni esportazione viene registrata nell'audit log. Vengono esportati solo dati anagrafici e consensi (nessun dato ottico).</p>
        <div className="flex gap-3">
          <Button data-testid="export-csv-btn" onClick={() => exportData("csv")} variant="outline"><Download className="h-4 w-4 mr-2" /> Esporta CSV</Button>
          <Button data-testid="export-xlsx-btn" onClick={() => exportData("xlsx")} className="bg-[#1E40AF] hover:bg-[#1D4ED8]"><Download className="h-4 w-4 mr-2" /> Esporta XLSX</Button>
        </div>
      </section>

      {/* GDPR full export */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-center gap-3 mb-1"><ShieldCheck className="h-5 w-5 text-emerald-600" /><h2 className="font-head text-lg font-semibold">Esportazione completa GDPR</h2></div>
        <p className="text-sm text-slate-500 mb-4">Esporta tutti i dati in formato JSON strutturato (diritto di portabilità e disaster recovery). Include clienti, prescrizioni, documenti, campagne, eventi, utenti (senza password) e log attività. Ogni esportazione è registrata nell'audit log. Per il singolo cliente usa il pulsante "Esporta GDPR" nella sua scheda.</p>
        <Button data-testid="export-completo-gdpr-btn" onClick={() => exportJson("/export/completo", "gdpr_database_completo.json")} variant="outline">
          <Download className="h-4 w-4 mr-2" /> Esporta intero database (JSON)
        </Button>
      </section>

      {/* JSON import / restore */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-center gap-3 mb-1"><Upload className="h-5 w-5 text-violet-600" /><h2 className="font-head text-lg font-semibold">Importazione / Ripristino JSON</h2></div>
        <p className="text-sm text-slate-500 mb-4">Ripristina i dati da un backup completo GDPR (file <code className="font-mono text-xs">gdpr_database_completo.json</code>). I record esistenti con lo stesso identificativo vengono aggiornati, i nuovi vengono aggiunti. Gli utenti non vengono importati (per non compromettere gli accessi). L'operazione è registrata nell'audit log.</p>
        <input ref={importRef} type="file" accept=".json,application/json" onChange={onImportSelected} className="hidden" data-testid="import-file-input" />
        <Button data-testid="import-json-btn" onClick={() => importRef.current?.click()} disabled={importing} className="bg-violet-600 hover:bg-violet-700 text-white">
          {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importazione…</> : <><Upload className="h-4 w-4 mr-2" /> Importa backup JSON</>}
        </Button>
      </section>

      {/* Users */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3"><UserCog className="h-5 w-5 text-[#1E40AF]" /><h2 className="font-head text-lg font-semibold">Gestione utenti</h2></div>
          <Button data-testid="new-user-btn" onClick={() => setOpen(true)} size="sm" className="bg-[#1E40AF] hover:bg-[#1D4ED8]"><UserPlus className="h-4 w-4 mr-2" /> Nuovo utente</Button>
        </div>
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left">
              <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Utente</th>
              <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Ruolo</th>
              <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Email</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-6 py-3.5 font-medium text-slate-900">{u.username} {u.name && <span className="text-slate-400 font-normal">· {u.name}</span>}</td>
                <td className="px-6 py-3.5">
                  {u.role === "admin"
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><ShieldCheck className="h-3 w-3 mr-1" /> Amministratore</Badge>
                    : <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100"><UserCog className="h-3 w-3 mr-1" /> Operatore</Badge>}
                </td>
                <td className="px-6 py-3.5 hidden md:table-cell text-slate-600">{u.email || "—"}</td>
                <td className="px-6 py-3.5 text-right">
                  {u.id !== user.id && (
                    <button data-testid={`delete-user-${u.id}`} onClick={() => delUser(u)} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Security & backup info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-3 mb-3"><ShieldAlert className="h-5 w-5 text-emerald-600" /><h2 className="font-head text-lg font-semibold">Sicurezza</h2></div>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Autenticazione con password sicure (bcrypt)</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Connessione HTTPS obbligatoria</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Sessioni sicure con cookie httpOnly</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Protezione da tentativi di forza bruta</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Log di accessi, modifiche ed esportazioni</li>
          </ul>
        </section>
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-3 mb-3"><Database className="h-5 w-5 text-[#1E40AF]" /><h2 className="font-head text-lg font-semibold">Backup & Disaster Recovery</h2></div>
          <p className="text-sm text-slate-600">Architettura MongoDB compatibile con backup automatici, ripristino dati e disaster recovery. I dati ottici sono archiviati in una collezione separata dall'anagrafica per la conformità GDPR.</p>
        </section>
      </div>

      {/* New user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-form">
          <DialogHeader><DialogTitle className="font-head">Nuovo utente</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Username *</Label><Input data-testid="user-username" value={form.username} onChange={(e) => set("username", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Nome completo</Label><Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" data-testid="user-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Password *</Label><Input type="password" data-testid="user-password" value={form.password} onChange={(e) => set("password", e.target.value)} /><p className="text-xs text-slate-400">Min 10 caratteri, con maiuscola, minuscola, numero e simbolo.</p></div>
            <div className="space-y-1.5">
              <Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operatore">Operatore</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-user-btn" onClick={createUser} disabled={saving} className="bg-[#1E40AF] hover:bg-[#1D4ED8]">{saving ? "Creazione…" : "Crea utente"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingFile} onOpenChange={(v) => !v && setPendingFile(null)}>
        <AlertDialogContent data-testid="import-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'importazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per ripristinare i dati dal file "{pendingFile?.name}". I record esistenti con lo stesso identificativo verranno aggiornati e i nuovi aggiunti. Consigliato effettuare prima un'esportazione di backup.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-import-btn" onClick={doImport} className="bg-violet-600 hover:bg-violet-700">Importa</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
